import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.primary.light,
    '&::before': {
      opacity: 1,
    },
  },
}));

interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  description?: string;
  icon?: React.ReactElement<{ style?: React.CSSProperties }>;
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

const MetricCard: React.FC<MetricCardProps> = (props) => {
  const {
    title,
    value,
    description,
    icon,
    color,
    trend = 'neutral',
    trendValue,
  } = props;
  const theme = useTheme();
  const defaultColor = color || theme.palette.primary.main;

  // Safe trend value with default
  // Set default trend if not provided
  const safeTrend: 'up' | 'down' | 'neutral' = trend || 'neutral';
  
  // Get color based on trend
  const trendColor = 
    safeTrend === 'up' ? theme.palette.success.main : 
    safeTrend === 'down' ? theme.palette.error.main : 
    theme.palette.text.secondary;
    
  // Safe trend value display
  const trendDisplay = trendValue ? `${safeTrend === 'up' ? '↑' : safeTrend === 'down' ? '↓' : '→'} ${trendValue}` : '';

  return (
    <StyledCard sx={{ '--card-accent': defaultColor }}>
      <Box display="flex" flexDirection="column" height="100%" position="relative">
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ 
                fontWeight: 600, 
                fontSize: '0.8125rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                opacity: 0.8
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                mt: 0.5,
                fontSize: '2.1rem', // Aumentado de ~1.75rem a ~2.1rem (5 puntos más)
                background: `linear-gradient(135deg, ${defaultColor}, ${theme.palette.mode === 'light' ? defaultColor : theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {value}
            </Typography>
          </Box>
          {icon && (
            <Box
              sx={{
                backgroundColor: `${defaultColor}15`,
                borderRadius: '12px',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: defaultColor,
                transition: 'all 0.3s ease',
                '& svg': {
                  fontSize: '1.5rem',
                },
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: `${defaultColor}25`,
                },
              }}
            >
              {icon && React.cloneElement(icon, { 
                style: { 
                  fontSize: '1.5rem',
                  filter: `drop-shadow(0 2px 4px ${defaultColor}40)`
                } 
              } as React.HTMLAttributes<SVGElement>)}
            </Box>
          )}
        </Box>
        
        {(description || trendDisplay) && (
          <Box mt="auto" pt={1} display="flex" alignItems="center">
            {trendDisplay && (
              <Box 
                component="span" 
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  mr: 1.5,
                  color: trendColor,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '& svg': {
                    fontSize: '1rem',
                    ml: 0.5,
                  },
                }}
              >
                {trendDisplay}
              </Box>
            )}
            {description && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  opacity: 0.8,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </StyledCard>
  );
};

export default MetricCard;
